import React, { useRef, useState, useEffect, Suspense } from 'react'
import { Form, Spin } from 'antd'
import './ReceivedInvoice.scss'
import { DrawerModule, CustomizeRenderEmpty } from '../index'
import {
  getFilteredRevenueListService,
  getPredictionDetailsById,
  updateRevenueDetails,
} from '../../utils/api/predictionAndInvoiceServices'
import { useDispatch, useSelector } from 'react-redux'
import {
  DEFAULT_PAGE_NO,
  DEFAULT_PAGE_SIZE,
  DEFAULT_REV_FILTER,
  STATUS,
  TOAST,
} from '../../utils/constants/constant'
import { staticOptionsSelector } from '../../redux/reducer/getOptionsSlice'
import SearchInvoiceField from '../SearchInvoiceField/SearchInvoiceField'
import { suggestionsOptionsApiService } from '../../utils/api/staticOptionsServices'
import { toastNotify } from '../../utils/helperFunctions/toastNotify'

const RevenueListTable = React.lazy(
  () => import('../RevenueListTable/RevenueListTable')
)

const ReceivedInvoice = ({ open, onClose }) => {
  const [dataSource, setDataSource] = useState({ revenueDetails: [] }) // Initialize with empty array
  const [isLoading, setIsLoading] = useState(false)
  const [autoSearchOptions, setAutoSearchOptions] = useState([])
  const [hasSearched, setHasSearched] = useState(false) // Track if user has performed a search
  const inputRef = useRef(null)
  const { projectId } = useSelector((state) => state.projectId)
  const { invoiceNumberOptions } = useSelector(staticOptionsSelector)
  const [searchFormInstance] = Form.useForm()
  const invoiceNumber = Form.useWatch('searchInvoice', searchFormInstance)
  const dispatch = useDispatch()

  useEffect(() => {
    inputRef.current.input.value = null
    // Reset to initial state when drawer is closed
    setDataSource({ revenueDetails: [] })
    setHasSearched(false)
    setIsLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose])

  const onSearchInvoiceNumber = async () => {
    setIsLoading(true)
    setHasSearched(true) // Mark that user has performed a search
    const response = await getFilteredRevenueListService(
      DEFAULT_PAGE_NO,
      DEFAULT_PAGE_SIZE,
      {
        ...DEFAULT_REV_FILTER,
        invoiceNumber: [invoiceNumber],
        receivedMonth: [],
      }
    )
    if (response?.status === STATUS.ok) {
      const data = response?.data?.data
      setDataSource(data || { revenueDetails: [] })
    } else {
      setDataSource({ revenueDetails: [] })
    }
    setIsLoading(false)
  }

  const fieldChangeHandler = async (field, value, record) => {
    const data =
      field === 'invoiceDueDate'
        ? {
            ...record,
            invoiceDetails: {
              ...record.invoiceDetails,
              invoiceDueDate: value,
            },
          }
        : { ...record, [field]: value }
    const response = await updateRevenueDetails(data)
    if (response?.status === STATUS.ok) {
      const data = response?.data?.data
      const updatedRevenueList = dataSource?.revenueDetails?.map((revenue) => {
        if (revenue?.billingId === data?.billingId) {
          return { ...data }
        } else {
          return revenue
        }
      })
      setDataSource((prevDatasource) => {
        return { ...prevDatasource, revenueDetails: updatedRevenueList }
      })
    } else {
      response?.data?.message && toastNotify(TOAST.error, response.data.message)
    }
  }

  const onCloseReceiveModule = () => {
    projectId && dispatch(getPredictionDetailsById(projectId))
    setDataSource({ revenueDetails: [] })
    setHasSearched(false)
    setIsLoading(false)
    onClose()
  }

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="spin-container">
          <Spin />
        </div>
      )
    }

    if (!hasSearched || dataSource?.revenueDetails?.length > 0) {
      return (
        <div className="receive-invoice-wrap">
          <RevenueListTable
            revenueList={dataSource}
            fieldChangeHandler={fieldChangeHandler}
            fetchRevenueList={onSearchInvoiceNumber}
            isFilterRequired={false}
            open={open}
          />
        </div>
      )
    }

    return (
      <CustomizeRenderEmpty setDataSource={setDataSource} inputRef={inputRef} />
    )
  }

  return (
    <DrawerModule
      className="invoice-received-wrapper"
      size="100%"
      title={'Search Invoices'}
      open={open}
      onClose={onCloseReceiveModule}
    >
      <Form
        form={searchFormInstance}
        className="mb-28"
        onFinish={(value) => {
          if (!value.searchInvoice) {
            setDataSource({ revenueDetails: [] })
            setHasSearched(false)
            return
          }
          onSearchInvoiceNumber()
        }}
      >
        <SearchInvoiceField
          label={'Search Invoice'}
          searchFormInstance={searchFormInstance}
          onClear={() => {
            setDataSource({ revenueDetails: [] })
            setHasSearched(false)
          }}
          invoiceNumberOptions={invoiceNumberOptions}
          inputRef={inputRef}
          onChange={async (value) => {
            const invoiceNumbers = await suggestionsOptionsApiService(
              'invoiceNumber',
              value
            )
            setAutoSearchOptions(invoiceNumbers)
          }}
          autoSearchOptions={autoSearchOptions}
          setAutoSearchOptions={setAutoSearchOptions}
        />
      </Form>

      <div className="receive-invoice">
        <Suspense
          fallback={
            <div className="spin-container">
              <Spin />
            </div>
          }
        >
          {renderContent()}
        </Suspense>
      </div>
    </DrawerModule>
  )
}

export default ReceivedInvoice
